import { ErrorMessage, Spinner } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTechProgramByIdQuery } from "@/hooks/api/techProgram.queries";
import {
  ArrowLeft,
  BookOpen,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Code2,
  FileText,
  Medal,
  Target,
  User,
  Users,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const TechProgramDetailPlaceholder = () => (
  <div className="flex justify-center py-16">
    <Spinner />
  </div>
);

const ActivityTechSkillsView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: program,
    isLoading,
    isError,
  } = useTechProgramByIdQuery(id || "");

  if (isLoading) return <TechProgramDetailPlaceholder />;
  if (isError || !program) {
    return (
      <ErrorMessage
        message="Failed to load tech skills program details."
        onRetry={() => navigate("/activities/tech-skills")}
      />
    );
  }

  // Derived mock data to match UI
  const enrolledCount = program.enrolled || program.students.length || 75;
  const completedCount = program.completed || 62;
  const inProgressCount = enrolledCount - completedCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/activities/tech-skills")}
            className="mt-1 h-8 w-8 rounded-full border border-gray-200 shadow-sm"
          >
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{program.name}</h1>
            <p className="text-gray-500">
              {program.vendor === "1" ||
              program.vendor === "CodeMaster Institute"
                ? "CodeMaster Institute"
                : `Vendor ID: ${program.vendor}`}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Badge
            variant="secondary"
            className="bg-gray-100 px-3 py-1 text-xs font-semibold capitalize text-gray-900 hover:bg-gray-200"
          >
            {program.status}
          </Badge>
          <Badge
            variant="outline"
            className="border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-900"
          >
            {program.mode}
          </Badge>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Users size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Enrolled</p>
              <p className="text-lg font-bold text-blue-600">{enrolledCount}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-600">
              <CheckCircle2 size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Completed</p>
              <p className="text-lg font-bold text-green-600">
                {completedCount}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 text-orange-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-trending-up"
              >
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                <polyline points="16 7 22 7 22 13" />
              </svg>
            </div>
            <div>
              <p className="text-xs text-gray-500">In Progress</p>
              <p className="text-lg font-bold text-orange-600">
                {inProgressCount}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50 text-purple-600">
              <Clock size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="font-semibold text-gray-900">{program.duration}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        {/* Row 1: Vendor, Schedule, Trainer */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <Building2 size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Vendor Information
              </h3>
            </div>
            <CardContent className="space-y-5 px-6 pb-6">
              <div>
                <p className="mb-1 text-sm text-gray-400">Vendor</p>
                <p className="font-semibold text-gray-900">
                  {program.vendor === "1" ||
                  program.vendor === "CodeMaster Institute"
                    ? "CodeMaster Institute"
                    : program.vendor}
                </p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-400">Address</p>
                <p className="text-sm text-gray-900">
                  101 Tech Plaza, San Francisco, CA 94105
                </p>
              </div>
              <hr className="border-gray-50" />
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                  <a
                    href="mailto:contact@codemaster.com"
                    className="hover:underline"
                  >
                    contact@codemaster.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <span>+1-555-789-0123</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <Calendar size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">Schedule</h3>
            </div>
            <CardContent className="space-y-5 px-6 pb-6">
              <div>
                <p className="mb-1 text-sm text-gray-400">Duration</p>
                <p className="font-semibold text-gray-900">
                  {program.duration}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="mb-1 text-xs text-gray-400">Start Date</p>
                  <p className="text-sm text-gray-900">
                    {new Date(program.startDate).toLocaleDateString("en-GB")}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-400">End Date</p>
                  <p className="text-sm text-gray-900">
                    {new Date(program.endDate).toLocaleDateString("en-GB")}
                  </p>
                </div>
              </div>

              <hr className="border-gray-50" />

              <div>
                <p className="mb-1 text-xs text-gray-400">Timing</p>
                <p className="text-sm text-gray-900">
                  Monday, Wednesday, Friday
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  6:00 PM - 9:00 PM
                </p>
              </div>

              <div className="pt-2">
                <div className="mb-2 flex items-end justify-between">
                  <span className="text-xs text-gray-400">Progress</span>
                  <span className="text-sm font-bold text-gray-900">
                    36/36 sessions
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-purple-600"
                    style={{ width: "100%" }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <Users size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Trainer & Coordinator
              </h3>
            </div>
            <CardContent className="space-y-5 px-6 pb-6">
              <div>
                <h4 className="mb-1 text-xs text-gray-400">Trainer</h4>
                <p className="text-base font-bold text-gray-900">
                  Mr. Robert Chen
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Full Stack Development & Cloud Architecture
                </p>
                <p className="mt-0.5 text-xs text-gray-400">15+ years</p>
                <div className="mt-3 flex flex-col gap-2">
                  <Badge
                    variant="outline"
                    className="w-fit border-gray-200 py-0.5 text-xs text-gray-600"
                  >
                    AWS Certified Solutions Architect
                  </Badge>
                  <Badge
                    variant="outline"
                    className="w-fit border-gray-200 py-0.5 text-xs text-gray-600"
                  >
                    Google Cloud Professional
                  </Badge>
                </div>
              </div>
              <hr className="border-gray-50" />
              <div>
                <h4 className="mb-1 text-xs text-gray-400">
                  College Coordinator
                </h4>
                <p className="text-base font-bold text-gray-900">
                  Prof. Sarah Johnson
                </p>
                <p className="mt-0.5 text-xs text-gray-500">Computer Science</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Program Details, Tech Stack */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <FileText size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Program Details
              </h3>
            </div>
            <CardContent className="space-y-6 p-6">
              <div>
                <h4 className="mb-3 font-bold text-gray-900">Description</h4>
                <p className="text-sm leading-relaxed text-gray-500">
                  {program.programDescription}
                </p>
              </div>
              <div>
                <h4 className="mb-3 font-bold text-gray-900">Prerequisites</h4>
                <ul className="ml-1 list-inside list-disc space-y-2 text-sm text-gray-500">
                  {program.prerequisites.split("\n").map((prereq, i) => (
                    <li key={i}>{prereq}</li>
                  ))}
                  <li>Familiarity with HTML/CSS</li>
                  <li>Understanding of JavaScript fundamentals</li>
                  <li>Problem-solving aptitude</li>
                </ul>
              </div>
              <div>
                <h4 className="mb-3 font-bold text-gray-900">
                  Learning Objectives
                </h4>
                <ul className="ml-1 list-inside list-disc space-y-2 text-sm text-gray-500">
                  {program.programObjectives.split("\n").map((obj, i) => (
                    <li key={i}>{obj}</li>
                  ))}
                  <li>Build RESTful APIs with Node.js and Express</li>
                  <li>Work with MongoDB and database design</li>
                  <li>Deploy applications to cloud platforms</li>
                  <li>Implement authentication and authorization</li>
                  <li>Follow industry-standard coding practices</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Code2 size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Technologies Stack
              </h3>
            </div>
            <CardContent className="space-y-6 p-6">
              <div>
                <h4 className="mb-3 text-sm font-bold text-gray-900">
                  Frontend
                </h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    "React",
                    "Redux",
                    "HTML5",
                    "CSS3",
                    "JavaScript ES6+",
                    "Bootstrap",
                    "Tailwind CSS",
                  ].map((tech) => (
                    <Badge
                      key={tech}
                      variant="secondary"
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="mb-3 text-sm font-bold text-gray-900">
                  Backend
                </h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Node.js",
                    "Express.js",
                    "RESTful APIs",
                    "JWT Authentication",
                  ].map((tech) => (
                    <Badge
                      key={tech}
                      variant="secondary"
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="mb-3 text-sm font-bold text-gray-900">
                  Database
                </h4>
                <div className="flex flex-wrap gap-2">
                  {["MongoDB", "Mongoose"].map((tech) => (
                    <Badge
                      key={tech}
                      variant="secondary"
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="mb-3 text-sm font-bold text-gray-900">Tools</h4>
                <div className="flex flex-wrap gap-2">
                  {["Git", "GitHub", "VS Code", "Postman", "npm"].map(
                    (tech) => (
                      <Badge
                        key={tech}
                        variant="secondary"
                        className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        {tech}
                      </Badge>
                    )
                  )}
                </div>
              </div>
              <div>
                <h4 className="mb-3 text-sm font-bold text-gray-900">
                  Deployment
                </h4>
                <div className="flex flex-wrap gap-2">
                  {["Heroku", "Netlify", "AWS S3"].map((tech) => (
                    <Badge
                      key={tech}
                      variant="secondary"
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Row 3: Curriculum Modules */}
        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
            <BookOpen size={18} className="text-gray-700" />
            <h3 className="text-lg font-bold text-gray-900">
              Curriculum Modules
            </h3>
          </div>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[
                { title: "HTML5 & CSS3 Fundamentals", duration: "1 week" },
                { title: "JavaScript ES6+", duration: "2 weeks" },
                { title: "React.js & Redux", duration: "3 weeks" },
                { title: "Node.js & Express", duration: "2 weeks" },
                { title: "MongoDB & Database Design", duration: "2 weeks" },
                { title: "RESTful APIs", duration: "1 week" },
                { title: "Authentication & Security", duration: "1 week" },
                { title: "Deployment & DevOps Basics", duration: "1 week" },
              ].map((module, i) => (
                <div
                  key={i}
                  className="flex flex-col justify-between rounded-xl border border-gray-100 p-4"
                >
                  <div>
                    <div className="mb-1 flex items-start gap-2">
                      <CheckCircle2
                        size={16}
                        className="mt-0.5 flex-shrink-0 text-green-500"
                      />
                      <h4 className="text-sm font-bold text-gray-900">
                        {module.title}
                      </h4>
                    </div>
                    <p className="ml-6 text-xs text-gray-500">
                      {module.duration}
                    </p>
                  </div>
                  <div className="mt-4">
                    <Badge
                      variant="secondary"
                      className="ml-6 bg-gray-100 text-[10px] text-gray-700"
                    >
                      Completed
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Row 4: Enrolled Students */}
        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
            <Users size={18} className="text-gray-700" />
            <h3 className="text-lg font-bold text-gray-900">
              Enrolled Students (3)
            </h3>
          </div>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  name: "Alex Johnson",
                  roll: "CSE2021001",
                  dept: "CSE",
                  attendance: "92%",
                  assignments: "12/12",
                  projects: "3/3",
                  score: "88",
                  finalProject: "E-commerce Platform with Payment Integration",
                  remarks:
                    "Excellent problem-solver, built impressive projects",
                },
                {
                  name: "Emma Watson",
                  roll: "IT2021045",
                  dept: "IT",
                  attendance: "95%",
                  assignments: "12/12",
                  projects: "3/3",
                  score: "95",
                  finalProject:
                    "Social Media Dashboard with Real-time Analytics",
                  remarks: "Outstanding performance, top of the class",
                },
                {
                  name: "Sophia Brown",
                  roll: "CSE2021023",
                  dept: "CSE",
                  attendance: "98%",
                  assignments: "12/12",
                  projects: "3/3",
                  score: "92",
                  finalProject:
                    "Task Management System with Team Collaboration",
                  remarks: "Strong technical skills, great team player",
                },
              ].map((student, idx) => (
                <div
                  key={idx}
                  className="flex h-full flex-col justify-between rounded-2xl border border-gray-100 p-6"
                >
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50 font-bold text-purple-600">
                          <User size={20} />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            {student.name}
                          </h4>
                          <div className="mt-0.5 text-[10px] text-gray-500">
                            {student.roll}
                          </div>
                          <div className="mt-1 flex">
                            <Badge
                              variant="outline"
                              className="border-gray-200 px-1.5 py-0 text-[10px] text-gray-500"
                            >
                              {student.dept}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-gray-700">
                        Completed
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="mt-4 space-y-2 text-xs text-gray-500">
                      <div className="flex justify-between">
                        <span>Attendance</span>
                        <span className="font-bold text-gray-900">
                          {student.attendance}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Assignments</span>
                        <span className="font-bold text-gray-900">
                          {student.assignments}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Projects</span>
                        <span className="font-bold text-gray-900">
                          {student.projects}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 rounded-xl border border-purple-100/50 bg-purple-50/50 p-3">
                      <p className="mb-1 text-xs text-gray-500">Score</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {student.score}
                      </p>
                    </div>

                    <div className="mt-4">
                      <p className="mb-1 text-[10px] text-gray-500">
                        Final Project
                      </p>
                      <p className="text-xs font-semibold text-gray-900">
                        {student.finalProject}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 rounded-lg border border-green-100/50 bg-green-50/50 p-2.5 text-xs font-medium text-green-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                      Certificate Issued
                    </div>

                    <div className="mt-2 rounded-lg border border-yellow-100/50 bg-yellow-50/50 p-2.5 text-xs text-yellow-700">
                      {student.remarks}
                    </div>
                  </div>

                  <div className="mt-4 border-t border-gray-50 pt-4">
                    <Button
                      variant="outline"
                      className="h-9 w-full rounded-xl border-gray-200 text-xs text-gray-700 shadow-sm"
                    >
                      View Profile
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Row 5: Assessment & Outcomes */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-gray-700"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
              <h3 className="text-lg font-bold text-gray-900">
                Assessment Methods
              </h3>
            </div>
            <CardContent className="px-6 pb-6">
              <div className="space-y-3">
                {[
                  "Weekly Coding Assignments (40%)",
                  "Mini Projects (30%)",
                  "Final Capstone Project (20%)",
                  "Attendance & Participation (10%)",
                ].map((method, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-3"
                  >
                    <CheckCircle2 size={16} className="text-blue-500" />
                    <span className="text-sm text-gray-700">{method}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <Medal size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Career Outcomes
              </h3>
            </div>
            <CardContent className="space-y-6 px-6 pb-6">
              <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                <p className="mb-1 text-xs text-gray-500">Industry Relevance</p>
                <p className="text-sm text-blue-900">
                  High - Full stack developers are in high demand across all
                  tech companies and startups.
                </p>
              </div>

              <div>
                <h4 className="mb-3 text-sm font-bold text-gray-900">
                  Expected Outcomes
                </h4>
                <div className="space-y-2">
                  {[
                    "Build full-stack web applications",
                    "Deploy to production environments",
                    "Work with modern development tools",
                    "Understand software development lifecycle",
                    "Ready for junior developer roles",
                  ].map((outcome, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle2
                        size={16}
                        className="mt-0.5 flex-shrink-0 text-green-500"
                      />
                      <span className="text-sm text-gray-600">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Row 6: Mapped Sections */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <Users size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Related Soft Skills
              </h3>
            </div>
            <CardContent className="space-y-3 px-6 pb-6">
              <div className="rounded-xl border border-yellow-100 bg-yellow-50/50 p-4">
                <p className="mb-2 text-sm font-semibold text-gray-900">
                  Effective Communication
                </p>
                <Badge
                  variant="secondary"
                  className="bg-gray-100 text-[10px] text-gray-700"
                >
                  Completed
                </Badge>
              </div>
              <div className="rounded-xl border border-yellow-100 bg-yellow-50/50 p-4">
                <p className="text-sm font-semibold text-gray-900">
                  Leadership Development
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <Target size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Related Internships
              </h3>
            </div>
            <CardContent className="space-y-3 px-6 pb-6">
              <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                <p className="mb-1 text-sm font-semibold text-gray-900">
                  Software Development Intern
                </p>
                <p className="text-[10px] text-gray-500">Google Inc.</p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 px-6 py-4">
              <Medal size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Student Placements
              </h3>
            </div>
            <CardContent className="space-y-3 px-6 pb-6">
              <div className="rounded-xl border border-green-100 bg-green-50/50 p-4">
                <p className="mb-1 text-sm font-semibold text-gray-900">
                  Emma Watson
                </p>
                <p className="text-[10px] text-gray-500">
                  Microsoft - Software Engineer
                </p>
              </div>
              <div className="rounded-xl border border-green-100 bg-green-50/50 p-4">
                <p className="mb-1 text-sm font-semibold text-gray-900">
                  Alex Johnson
                </p>
                <p className="text-[10px] text-gray-500">
                  Google Inc. - Software Engineer
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ActivityTechSkillsView;
